import type { JSX } from 'react';
import type { AvailableUnitGroup } from '@/components/public/units';
import type { AvailabilitiesResultsContent } from '@/lib/types/siteSettings';
import { resolveAvailabilityMapCoordinates } from '../availabilitiesMapLocations';
import { AvailabilityGroup } from './AvailabilityGroup';
import { AvailabilitiesMapList } from './AvailabilitiesMapList';
import type { AvailabilitiesViewMode } from './AvailabilitiesViewToggle';

interface AvailabilitiesResultsProps {
  groups: AvailableUnitGroup[];
  availableGroupsCount: number;
  hasActiveFilters: boolean;
  onResetFilters: () => void;
  viewMode: AvailabilitiesViewMode;
  content: AvailabilitiesResultsContent;
  mapListContent: Parameters<typeof AvailabilitiesMapList>[0]['content'];
  listContent: Parameters<typeof AvailabilityGroup>[0]['content'];
}

export function AvailabilitiesResults({
  groups,
  availableGroupsCount,
  hasActiveFilters,
  onResetFilters,
  viewMode,
  content,
  mapListContent,
  listContent,
}: AvailabilitiesResultsProps): JSX.Element {
  const hasMapPoints = groups.some(
    (group) => resolveAvailabilityMapCoordinates(group.property) !== null,
  );

  if (groups.length === 0) {
    const showNoMatches = hasActiveFilters && availableGroupsCount > 0;
    return (
      <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-surface-subtle bg-surface-subtle/40 p-6 text-center">
        <p className="text-sm text-text-primary/70">
          {showNoMatches ? content.noMatchesMessage : content.noAvailabilityMessage}
        </p>
        {showNoMatches ? (
          <button
            type="button"
            onClick={onResetFilters}
            className="min-h-11 rounded-full bg-brand-primary px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-primary/90"
          >
            {content.clearFiltersLabel}
          </button>
        ) : null}
      </div>
    );
  }

  if (viewMode === 'map' && hasMapPoints) {
    return <AvailabilitiesMapList groups={groups} content={mapListContent} />;
  }

  return (
    <div className="flex flex-col gap-8 md:gap-10">
      {groups.map((group) => (
        <AvailabilityGroup key={group.property.id} group={group} content={listContent} />
      ))}
    </div>
  );
}
